import type { FlowEditorNode, FlowEditorState, FlowGridItem, FlowGridLayouts, FlowNodeKind, WidgetConfigState, WidgetTab } from "./types";
import { DonateWidget } from "services/main/donate.service";
import { DEFAULT_VIEWPORT, flowNodePalette, viewportPresets } from "./constants";
import { getAlertLayoutPresetDefinition } from "./constants";
import { getDefaultGridSizeForSettings, getFlowViewport, getGridConfig, getGridRows } from "./flowViewportUtils";
import { clampGridItem, createDefaultGridLayouts, getGridItemPixels } from "./flowGridUtils";
import { isGridItemBottomLeft, normalizeWidgetAnchor, recenterAnchoredNodes } from "./flowAnchorUtils";
import { constrainFlowStateToEnabledObjects, createDefaultFlowState, isFlowNodeKind } from "./flowNodeStateUtils";
import { getDefaultNodeSettings, getFlowNodeSize } from "./flowNodeDefaults";
import { getFlowKindFromGridItem, getGridLayoutItemId, getFrontendPlacement } from "./flowWidgetKindUtils";
import { isEnabledWidgetTab, toBooleanValue } from "./widgetUtils";
export const normalizeApiGridItem = (
  layout: any,
  viewport: FlowEditorState["viewport"],
  kind: FlowNodeKind,
  settings?: Record<string, any>,
) => {
  if (!layout) return undefined;
  const fallbackSize = getDefaultGridSizeForSettings(
    kind,
    viewport.device,
    settings,
  );
  const normalizedItem = clampGridItem(
    {
      x: Number(layout.x) || 0,
      y: Number(layout.y) || 0,
      w: Number(layout.w) || fallbackSize.w,
      h: Number(layout.h) || fallbackSize.h,
    },
    viewport,
  );

  if (kind !== "alert") return normalizedItem;

  return clampGridItem(
    {
      ...normalizedItem,
      w: Math.max(normalizedItem.w, fallbackSize.w),
      h: Math.max(normalizedItem.h, fallbackSize.h),
    },
    viewport,
  );
};

export const normalizeFrontendPlacement = (
  placement: any,
  viewport: FlowEditorState["viewport"],
  kind: FlowNodeKind,
  settings?: Record<string, any>,
) => {
  if (!placement || typeof placement !== "object") return undefined;

  const config = getGridConfig(viewport);
  const targetRows = getGridRows(viewport);
  const sourceCols = Math.max(Number(placement.cols) || config.cols, 1);
  const sourceRows = Math.max(Number(placement.rows) || targetRows, 1);
  const fallbackSize = getDefaultGridSizeForSettings(
    kind,
    viewport.device,
    settings,
  );
  const sourceW = Math.max(Number(placement.w) || fallbackSize.w, 1);
  const sourceH = Math.max(Number(placement.h) || fallbackSize.h, 1);
  const rawW = Math.max(Math.round((sourceW / sourceCols) * config.cols), 1);
  const rawH = Math.max(Math.round((sourceH / sourceRows) * targetRows), 1);
  const w = kind === "alert" ? Math.max(rawW, fallbackSize.w) : rawW;
  const h = kind === "alert" ? Math.max(rawH, fallbackSize.h) : rawH;

  return clampGridItem(
    {
      x: Math.round(((Number(placement.x) || 0) / sourceCols) * config.cols),
      y: Math.round(((Number(placement.y) || 0) / sourceRows) * targetRows),
      w,
      h,
    },
    viewport,
  );
};

export const getDefaultCenteredGridItem = (
  kind: FlowNodeKind,
  viewport: FlowEditorState["viewport"],
  settings?: Record<string, any>,
): FlowGridItem => {
  const size = getDefaultGridSizeForSettings(kind, viewport.device, settings);
  const rows = getGridRows(viewport);

  return clampGridItem(
    {
      x: Math.max(
        Math.floor((getGridConfig(viewport.device).cols - size.w) / 2),
        0,
      ),
      y: Math.max(Math.floor((rows - size.h) / 2), 0),
      w: size.w,
      h: size.h,
    },
    viewport,
  );
};

export const normalizeGridItemLayouts = (
  gridLayout: DonateWidget["gridLayout"] | undefined,
  item: any,
  itemId: string,
  kind: FlowNodeKind,
  settings?: Record<string, any>,
): FlowGridLayouts => {
  const placement = getFrontendPlacement(item);
  const allLayouts = viewportPresets
    .map((preset) => {
      const viewport = getFlowViewport({ device: preset.key });
      const layout = gridLayout?.layouts?.[preset.key]?.find(
        (entry) => String(entry?.i) === itemId,
      );
      return (
        normalizeApiGridItem(layout, viewport, kind, settings) ||
        normalizeFrontendPlacement(placement, viewport, kind, settings)
      );
    })
    .filter(Boolean) as FlowGridItem[];
  let previousLayout = allLayouts[0];

  return viewportPresets.reduce((layouts, preset) => {
    const viewport = getFlowViewport({ device: preset.key });
    const layout = gridLayout?.layouts?.[preset.key]?.find(
      (entry) => String(entry?.i) === itemId,
    );
    const directLayout =
      normalizeApiGridItem(layout, viewport, kind, settings) ||
      normalizeFrontendPlacement(placement, viewport, kind, settings);
    const nextLayout =
      directLayout ||
      (previousLayout
        ? normalizeApiGridItem(previousLayout, viewport, kind, settings) ||
          getDefaultCenteredGridItem(kind, viewport, settings)
        : getDefaultCenteredGridItem(kind, viewport, settings));

    previousLayout = nextLayout;

    return {
      ...layouts,
      [preset.key]: nextLayout,
    };
  }, {} as FlowGridLayouts);
};

export const normalizeFlowNode = (node: any, index: number): FlowEditorNode => {
  const rawKind = node?.data?.kind || node?.type;
  const kind = isFlowNodeKind(rawKind) ? rawKind : "text";
  const paletteItem = flowNodePalette.find((item) => item.kind === kind);
  const nodeSize = getFlowNodeSize(kind);
  const settings = { ...(node?.data?.settings || node?.data?.config || {}) };

  if (kind === "alert") {
    const preset = getAlertLayoutPresetDefinition(
      settings.layoutPreset || settings.layout,
      settings.theme,
    );
    settings.layoutPreset = preset.key;
    settings.theme = settings.theme || preset.theme;
    settings.backgroundStyle = "none";
    settings.effect = "none";
    settings.iconMode = "image";
    settings.showIcon = toBooleanValue(settings.showIcon, preset.showIcon);
  }
  const layouts =
    node?.data?.layouts || createDefaultGridLayouts(kind, settings);

  return {
    id: String(node?.id || `node-${index + 1}`),
    type: String(node?.type || kind || "text"),
    position: {
      x: Number(node?.position?.x) || 0,
      y: Number(node?.position?.y) || 0,
    },
    data: {
      ...(node?.data || {}),
      kind,
      label: String(node?.data?.label || paletteItem?.label || "Node"),
      text:
        node?.data?.text === undefined || node?.data?.text === null
          ? ""
          : String(node.data.text),
      color: String(node?.data?.color || paletteItem?.color || "#168FEB"),
      width: Number(node?.data?.width) || nodeSize.width,
      height: Number(node?.data?.height) || nodeSize.height,
      imageUrl: node?.data?.imageUrl,
      layouts,
      settings,
    },
  };
};

export const normalizeGridLayoutNode = (
  item: any,
  index: number,
  gridLayout: DonateWidget["gridLayout"] | undefined,
  config: WidgetConfigState,
): FlowEditorNode => {
  const id = getGridLayoutItemId(item, index);
  const kind = getFlowKindFromGridItem(item);
  const paletteItem = flowNodePalette.find((palette) => palette.kind === kind);
  const data = item?.data || {};
  const itemStyles = item?.styles || {};
  const settings = {
    ...getDefaultNodeSettings(kind, config),
    ...(item?.settings || {}),
  };

  if (kind === "alert" && !settings.messageTemplate && data.template) {
    settings.messageTemplate = data.template;
  }

  if (kind === "alert" && !settings.textColor && itemStyles.textColor) {
    settings.textColor = itemStyles.textColor;
  }

  if (kind === "alert" && !settings.iconUrl && data.imageUrl) {
    settings.iconUrl = data.imageUrl;
  }

  if (kind === "alert") {
    const preset = getAlertLayoutPresetDefinition(
      settings.layoutPreset || settings.layout,
      settings.theme,
    );
    settings.layoutPreset = preset.key;
    settings.theme = settings.theme || preset.theme;
    settings.backgroundStyle = "none";
    settings.effect = "none";
    settings.iconMode = "image";
    settings.showIcon = toBooleanValue(settings.showIcon, preset.showIcon);
  }

  if (kind === "image" && !settings.imageUrl && data.imageUrl) {
    settings.imageUrl = data.imageUrl;
  }

  const layouts = normalizeGridItemLayouts(
    gridLayout,
    item,
    id,
    kind,
    settings,
  );
  const viewport = getFlowViewport({ device: DEFAULT_VIEWPORT.key });
  const gridItem =
    layouts[viewport.device] ||
    getDefaultCenteredGridItem(kind, viewport, settings);
  const pixels = getGridItemPixels(gridItem, viewport);

  settings.anchor =
    normalizeWidgetAnchor(settings.anchor) === "free" &&
    isGridItemBottomLeft(gridItem, viewport)
      ? "bottom"
      : normalizeWidgetAnchor(settings.anchor);

  return {
    id,
    type: kind,
    position: {
      x: pixels.left,
      y: pixels.top,
    },
    data: {
      kind,
      label: String(item?.title || data.label || paletteItem?.label || "Node"),
      text: String(data.text || data.template || ""),
      color: String(itemStyles.color || paletteItem?.color || "#168FEB"),
      width: Number(itemStyles.width) || pixels.width,
      height: Number(itemStyles.height) || pixels.height,
      imageUrl: data.imageUrl || settings.imageUrl,
      layouts,
      settings,
    },
  };
};

export const normalizeFlowState = (
  reactFlow: DonateWidget["reactFlow"] | undefined,
  tab: WidgetTab,
  config: WidgetConfigState,
): FlowEditorState => {
  if (Array.isArray(reactFlow?.nodes) && reactFlow.nodes.length) {
    const viewport = getFlowViewport(reactFlow.viewport);

    return constrainFlowStateToEnabledObjects(
      {
        nodes: recenterAnchoredNodes(
          reactFlow.nodes.map(normalizeFlowNode),
          viewport,
        ),
        edges: Array.isArray(reactFlow.edges) ? reactFlow.edges : [],
        viewport,
      },
      tab,
      config,
    );
  }

  return createDefaultFlowState(
    isEnabledWidgetTab(tab) ? tab : "alert",
    config,
  );
};

export const normalizeGridState = (
  gridLayout: DonateWidget["gridLayout"] | undefined,
  reactFlow: DonateWidget["reactFlow"] | undefined,
  tab: WidgetTab,
  config: WidgetConfigState,
): FlowEditorState => {
  if (Array.isArray(gridLayout?.items) && gridLayout.items.length) {
    const viewport = getFlowViewport({ device: DEFAULT_VIEWPORT.key });

    return constrainFlowStateToEnabledObjects(
      {
        nodes: recenterAnchoredNodes(
          gridLayout.items.map((item, index) =>
            normalizeGridLayoutNode(item, index, gridLayout, config),
          ),
          viewport,
        ),
        edges: [],
        viewport,
      },
      tab,
      config,
    );
  }

  return normalizeFlowState(reactFlow, tab, config);
};
